// frontend/src/lib/novedades.ts
// Códigos institucionales de novedad (ACA, ASA, RMH, ART, LE, PE): derivación
// desde el motivo registrado, paleta de chips, orden del catálogo de tipos de
// licencia y utilidades de rangos de fechas compartidas por HistorialModal y
// ReportesTab.

import type { CodigoNovedad, TipoLicencia } from './api';

export const CODIGOS_NOVEDAD: CodigoNovedad[] = ['ACA', 'ASA', 'RMH', 'ART', 'LE', 'PE'];

export const NOMBRE_CODIGO: Record<CodigoNovedad, string> = {
  ACA: 'Ausente con aviso',
  ASA: 'Ausente sin aviso',
  RMH: 'Licencia por Enfermedad (R.M.H)',
  ART: 'A.R.T.',
  LE: 'Licencia Especial',
  PE: 'Permiso Especial',
};

// Motivos del sheet de CadeteCard que generan licencia automática → código del
// tipo de licencia en el catálogo. Sin fallback: cualquier otro motivo (p. ej.
// "Ausente con/sin aviso") registra solo la inasistencia del día.
export const LICENCIA_POR_MOTIVO: Record<string, 'RMH' | 'ART' | 'LE' | 'PE'> = {
  'Licencia por Enfermedad - R.M.H': 'RMH',
  'A.R.T.': 'ART',
  'Licencia Especial': 'LE',
  'Permiso Especial': 'PE',
};

// Orden de presentación de los tipos de licencia: los institucionales primero
const ORDEN_TIPOS = ['RMH', 'ART', 'LE', 'PE'];

/** Tipos de licencia con `activo !== false`, ordenados RMH, ART, LE, PE y luego alfabético. */
export function tiposLicenciaActivosOrdenados<T extends Pick<TipoLicencia, 'codigo' | 'nombre'> & { activo?: boolean }>(tipos: T[]): T[] {
  return tipos
    .filter((t) => t.activo !== false)
    .slice()
    .sort((a, b) => {
      const ia = ORDEN_TIPOS.indexOf(String(a.codigo).toUpperCase());
      const ib = ORDEN_TIPOS.indexOf(String(b.codigo).toUpperCase());
      const ra = ia === -1 ? ORDEN_TIPOS.length : ia;
      const rb = ib === -1 ? ORDEN_TIPOS.length : ib;
      if (ra !== rb) return ra - rb;
      return String(a.nombre || '').localeCompare(String(b.nombre || ''), 'es', { sensitivity: 'base' });
    });
}

/**
 * Código de novedad según el motivo registrado en CadeteCard
 * (p. ej. "Licencia por Enfermedad - R.M.H (Gripe)", "Permiso Especial: Art. 56 …").
 * Devuelve undefined si el motivo no coincide con ninguno conocido.
 * Misma lógica que usaba la Nómina de Presentismo (ReportesTab.codigoAusencia).
 */
export function codigoDeMotivo(motivo: string | null | undefined): CodigoNovedad | undefined {
  const m = (motivo || '').trim().toLowerCase();
  if (m.startsWith('ausente sin aviso')) return 'ASA';
  if (m.startsWith('ausente con aviso')) return 'ACA';
  if (m.includes('r.m.h') || m.includes('rmh')) return 'RMH';
  // "A.R.T." pero no un artículo reglamentario ("Art. 50 inc. …")
  if (m.startsWith('a.r.t') || (/^art\b/.test(m) && !/^art\.?\s*\d/.test(m))) return 'ART';
  if (m.startsWith('licencia especial')) return 'LE';
  if (m.startsWith('permiso especial')) return 'PE';
  return undefined;
}

/** Código de un registro: el que informa el backend o, si falta, el derivado del motivo (solo ausentes). */
export function codigoDeRegistro(r: { estado?: string; motivo?: string | null; codigo?: CodigoNovedad | null }): CodigoNovedad | undefined {
  if (r.codigo) return r.codigo;
  if (r.estado === 'ausente') return codigoDeMotivo(r.motivo);
  return undefined;
}

export interface EstiloChip { bg: string; color: string; border: string }

// Paleta de los chips de código: misma familia que la píldora de estado
// (ausente rojo, tarde dorado) y violeta para las licencias LE/PE (el que usa
// la tarjeta para "Licencia (XXX)").
export function estiloCodigo(codigo: string | null | undefined): EstiloChip {
  switch ((codigo || '').toUpperCase()) {
    case 'ASA':
      return { bg: '#ba1a1a', color: '#ffffff', border: '#93000a' };
    case 'ACA':
      return { bg: '#ffdad6', color: '#93000a', border: 'rgba(186,26,26,0.25)' };
    case 'RMH':
    case 'ART':
      return { bg: '#fed65b', color: '#745c00', border: 'rgba(115,92,0,0.25)' };
    case 'LE':
    case 'PE':
      return { bg: '#f3e8ff', color: '#6b21a8', border: '#d8b4fe' };
    default:
      return { bg: '#eae8e7', color: '#43474f', border: '#c3c6d1' };
  }
}

export function estiloEstado(estado: string): EstiloChip {
  if (estado === 'presente') return { bg: '#dbeafe', color: '#1e40af', border: '#bfdbfe' };
  if (estado === 'tarde') return { bg: '#fed65b', color: '#745c00', border: 'rgba(115,92,0,0.2)' };
  return { bg: '#ffdad6', color: '#93000a', border: 'rgba(186,26,26,0.2)' };
}

// ─── Fechas (YYYY-MM-DD) ────────────────────────────────────────────────────

export const MAX_DIAS_PERIODO = 366;

export function esFechaISO(v: string | null | undefined): v is string {
  return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);
}

/** Diferencia en días (hasta − desde), ambas YYYY-MM-DD; NaN si alguna es inválida. */
export function diasEntre(desde: string, hasta: string): number {
  if (!esFechaISO(desde) || !esFechaISO(hasta)) return NaN;
  const a = Date.UTC(+desde.slice(0, 4), +desde.slice(5, 7) - 1, +desde.slice(8, 10));
  const b = Date.UTC(+hasta.slice(0, 4), +hasta.slice(5, 7) - 1, +hasta.slice(8, 10));
  return Math.round((b - a) / 86400000);
}

/** Valida un rango: devuelve el mensaje de error o null si es válido. */
export function validarRango(desde: string, hasta: string, maxDias = MAX_DIAS_PERIODO): string | null {
  if (!esFechaISO(desde) || !esFechaISO(hasta)) return 'Seleccione las fechas Desde y Hasta.';
  const d = diasEntre(desde, hasta);
  if (d < 0) return 'La fecha "Desde" no puede ser posterior a "Hasta".';
  if (d + 1 > maxDias) return `El período no puede superar ${maxDias} días.`;
  return null;
}

/** Suma `dias` (puede ser negativo) a una fecha YYYY-MM-DD. */
export function sumarDias(fecha: string, dias: number): string {
  const d = new Date(+fecha.slice(0, 4), +fecha.slice(5, 7) - 1, +fecha.slice(8, 10) + dias);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/** Primer y último día del mes al que pertenece `fecha` (YYYY-MM-DD). */
export function rangoMes(fecha: string): { desde: string; hasta: string } {
  const y = +fecha.slice(0, 4);
  const m = +fecha.slice(5, 7);
  const ultimo = new Date(y, m, 0).getDate();
  const mm = String(m).padStart(2, '0');
  return { desde: `${y}-${mm}-01`, hasta: `${y}-${mm}-${String(ultimo).padStart(2, '0')}` };
}

/** 'YYYY-MM-DD' → 'DD/MM/YYYY' (devuelve el valor original si no es una fecha ISO). */
export function fechaCorta(fecha: string | null | undefined): string {
  if (!esFechaISO(fecha)) return fecha || '';
  return `${fecha.slice(8, 10)}/${fecha.slice(5, 7)}/${fecha.slice(0, 4)}`;
}

/** Licencia solapada con [desde, hasta]; fecha_fin null = abierta (hasta alta). */
export function licenciaSolapa(lic: { fecha_inicio?: string; fecha_fin?: string | null }, desde: string, hasta: string): boolean {
  if (!lic.fecha_inicio) return false;
  return lic.fecha_inicio <= hasta && (!lic.fecha_fin || lic.fecha_fin >= desde);
}
